import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import { dropKilogramRows } from '@features/quotations/utils/lineItem';
import type { SalesOrderLineItemDto } from '../types/salesOrder.types';
import { asPieces } from '../utils/lineWeight';

// SO line-item extraction wire logic. Lifted out of SalesOrderIntakePanel
// (86d3a2agk) so the salesOrderStore can own the fetch + apply, letting the
// re-extract modal close immediately and run in the background — mirroring the
// quotation store's extractIntoDraft. The panel is now input-collection only.

interface MatchedLineItemRaw {
  rawDescription: string | null;
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: number | null; // backend ships int? → number on the wire
  isWeldable: boolean;
  quantity: number | null;
  unit: string;
  notes: string | null;
  matchStatus: string | null;
  matchedItemId: string | null;
  matchedSalesDescription: string | null;
  totalWeightKg: number | null; // backend field name; maps to FE weightKg
  weightPerPcKg: number | null; // matched SKU per-piece weight (86d3dxnd7)
  basePricePerPc: number | null;
  averagePricePerPc: number | null;
  promotedFromGrade: number | null;
}

interface MatchedQuotationGroupRaw {
  lineItems: MatchedLineItemRaw[];
}

interface MatchingResultDto {
  quotations: MatchedQuotationGroupRaw[];
}

export interface IntakeSource {
  text: string;
  files: File[];
}

export interface ExtractedSalesOrderLines {
  items: SalesOrderLineItemDto[];
  /** kg lines dropped because the same document also listed bars in pieces (14ykpqtv3qw). */
  droppedKgLineCount: number;
}

/**
 * Run the AI extraction for a pasted-text + uploaded-files/screenshots source and return
 * the matched line items — always as pieces (14ykpqtv3qw, see applySalesOrderUnitRule). Sends every source together in ONE /api/intake/extract call so
 * the model treats them as a single set and merges duplicates across sources — rather than
 * the old per-source calls that concatenated results and double-counted overlaps
 * (86d3hdy52). Throws on a failed response so the caller (store) can surface the error and
 * skip applying.
 */
export async function extractLineItemsFromSource(
  source: IntakeSource,
): Promise<ExtractedSalesOrderLines> {
  const text = source.text.trim();
  const hasText = text.length > 0;
  if (!hasText && source.files.length === 0) return { items: [], droppedKgLineCount: 0 };

  const formData = new FormData();
  if (hasText) formData.append('text', text);
  for (const file of source.files) {
    formData.append('files', file);
  }

  const { data } = await apiClient.post<ApiResponse<MatchingResultDto>>(
    '/intake/extract',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180_000 },
  );
  if (data.success && data.data) {
    return applySalesOrderUnitRule(mapMatchingResultToLineItems(data.data));
  }
  throw new Error(data.message || 'Extraction failed');
}

/** Join uploaded file names into the draft's extractionFileName, capped at `max`. */
export function joinFileNamesCapped(names: string[], max = 256): string {
  const joined = names.join(', ');
  if (joined.length <= max) return joined;
  const kept: string[] = [];
  for (const name of names) {
    const remaining = names.length - kept.length;
    const tail = ` (+${remaining} more)`;
    const candidate = kept.length ? `${kept.join(', ')}, ${name}` : name;
    if (candidate.length + tail.length > max) break;
    kept.push(name);
  }
  const remaining = names.length - kept.length;
  const base = kept.join(', ');
  const out =
    remaining > 0 ? (base ? `${base} (+${remaining} more)` : `(+${remaining} more)`) : base;
  return out.length <= max ? out : out.slice(0, max);
}

/**
 * 14ykpqtv3qw — sales-order lines are pieces (finishing 86d3dxnd7's UI-only lock). The shared extraction keeps
 * kg lines (quotations drop them on their side), so the rule for sales orders lives here:
 *  - a document listing bars in pieces AND kg describes the same bars twice: drop the kg lines, as quotations do
 *    (14ykpqtr22u) — submit sums quantities per item, so keeping both would add kilograms to the piece counts;
 *  - a kg-only document keeps its lines, read as pieces (owner, 2026-10-14: on an SO source a kg label is most
 *    likely a misread — prod SO 227 was). originalSpecJson keeps the source unit for the "listed in kg" warning.
 * Every kept line is stored as pcs with its weight derived — never weight = quantity.
 */
export function applySalesOrderUnitRule(lines: SalesOrderLineItemDto[]): ExtractedSalesOrderLines {
  const hasPieces = lines.some((li) => li.unit !== 'kgs');
  const { rows, droppedKgLineCount } = hasPieces
    ? dropKilogramRows(lines)
    : { rows: lines, droppedKgLineCount: 0 };
  return { items: rows.map(asPieces), droppedKgLineCount };
}

function mapMatchingResultToLineItems(result: MatchingResultDto): SalesOrderLineItemDto[] {
  const groups = result.quotations ?? [];
  return groups.flatMap((g, gi) =>
    (g.lineItems ?? []).map((li, idx) => {
      const originalSpec = {
        diameterMm: li.diameterMm,
        lengthM: li.lengthM,
        gradeCode: li.gradeCode != null ? String(li.gradeCode) : null,
        isWeldable: li.isWeldable,
        quantity: li.quantity,
        unit: li.unit || 'pcs',
      };
      return {
        id: 0,
        clientId:
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `x-${gi}-${idx}-${Date.now().toString(36)}`,
        sortOrder: idx,
        diameterMm: li.diameterMm,
        lengthM: li.lengthM,
        gradeCode: li.gradeCode != null ? String(li.gradeCode) : null,
        isWeldable: li.isWeldable,
        quantity: li.quantity,
        unit: li.unit || 'pcs',
        weightKg: li.totalWeightKg,
        weightPerPcKg: li.weightPerPcKg,
        matchStatus: li.matchStatus,
        rawDescription: li.rawDescription,
        notes: li.notes,
        matchedSalesDescription: li.matchedSalesDescription,
        matchedItemId: li.matchedItemId,
        priceLevel: null,
        basePricePerPc: li.basePricePerPc,
        averagePricePerPc: li.averagePricePerPc,
        originalSpecJson: JSON.stringify(originalSpec),
        isEdited: false,
        promotedFromGrade: li.promotedFromGrade,
      };
    }),
  );
}
